import React, { useContext, useEffect, useMemo, useCallback } from 'react';
import { useSelector, connect } from 'react-redux';
import { compose } from 'recompose';
import styled from 'styled-components/macro';
import { RootState } from 'redux/rootReducer';
import { PromiseState } from 'react-refetch';
import { withEntityContext } from 'context/entityContext';
import { EntityContext } from 'context/entityContext';
import { selectAllCustomers, loadCustomers, isCustomersLoaded } from 'redux/reducers/customers';
import { loadCountries } from 'redux/reducers/countries';

import getAllowedCustomerFilters from 'helpers/getAllowedCustomerFilters';
import getInitialSalesType from 'helpers/getInitialSalesType';
import getCustomerFilters from 'helpers/getCustomerFilters';
import { tooltipContent } from 'helpers/getTooltipContent';
import stringToSlug from 'helpers/stringToSlug';
import formatDate from 'helpers/formatDate';
import apiConnect from 'helpers/apiConnect';

import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import useDateRangeKPI from 'hooks/useDateRangeKPI';
import useExportURL from 'hooks/useExportURL';
import useMedia from 'hooks/useMedia';

import getTerritoryKPIURL from 'url-constructors/getTerritoryKPIURL';
import getStreamingKPIURL from 'url-constructors/getStreamingKPIURL';
import { IEntities } from 'components/data-containers/KPIs/types';
import { ICustomer } from 'redux/reducers/customers';
import { EntityTypes } from 'context/entityContext';
import {
  ITopTerritoriesEntityCountryItem,
  ITopTerritoriesEntity,
} from '../shared/TerritoryBlock/types';

import { ReactComponent as StreamingIcon } from 'icons/topics/streaming.svg';
import { ITopicFilterState } from 'components/TopicFilters/shared';
import { ErrorMessage } from 'components/Error/ErrorContent/ErrorContent';
import KeyValueGroup from 'components/base/KeyValueGroup/KeyValueGroup';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import StreamingKPILoader from './StreamingKPILoader';
import KPIWrapper from '../KPIWrapper/KPIWrapper';
import { Container, KPIList, KPIListItem, Header, DateRange } from '../shared';
import KPILoader from 'components/utility/ContentLoader/KPILoader/KPILoader';
import mapKPIData from '../mappings/mapKPIData';
import DataWarning from 'components/base/DataWarning/DataWarning';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import TopTerritories from '../shared/TerritoryBlock/TopTerritories';
import { TopCountry } from '../shared/TerritoryBlock/TopCountry';
import Box from 'components/elements/Box';
import { InfoIcon } from './styles';
import { IToolbarItemType } from 'components/utility/GenericComponent/GenericComponent';

const DataWrapper = styled('div')<any>`
  ${({ isMobileView }): string =>
    isMobileView ? 'padding-bottom: 1.2em;' : ''}
  ${({ isHorizontalLineShown, isMobileView }): string =>
    isHorizontalLineShown &&
    `
    border-${isMobileView ? 'bottom' : 'right'}: 1px solid #E8EBED;
  `}
`;

interface IStreamingKPIsProps {
  allowedFilters: string[];
  glossarySlug: string;
  fetchStreamingData: Function;
  loadCountries: Function;
  loadCustomers: Function;
  streamingData: PromiseState<any>;
  topCountryThisWeekFetch: PromiseState<IEntities>;
  topCountryLastFullWeekFetch: PromiseState<IEntities>;
  topCountryReleaseToDateFetch: PromiseState<IEntities>;
  topTerritoriesRef: any;
  isCustomersLoaded: boolean;
}

const StreamingKPIs: React.FC<IStreamingKPIsProps> = (props) => {
  const isMobileView = useMedia('(max-width: 50em)');
  const {
    allowedFilters,
    glossarySlug,
    fetchStreamingData,
    streamingData = PromiseState.create(),
    loadCountries,
    loadCustomers,
    topCountryThisWeekFetch,
    topCountryLastFullWeekFetch,
    topCountryReleaseToDateFetch,
    topTerritoriesRef,
  } = props;

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const ctx = useContext(EntityContext);
  const {
    compareMode,
    countryIds,
    entityIds,
    entityType,
    filters,
    dataColors,
    customRegionFilter,
  } = ctx;

  const colors = compareMode ? dataColors : ['#001247'];

  const countriesMetadata = useSelector(
    (state: RootState) => state.countries.data
  );

  const compareArray = compareMode === 'country' ? countryIds : entityIds;
  const streamingExportUrl = useExportURL(streamingData);

  const warningMessage = filters?.streaming
    ? filters.streaming === -100
      ? 'Note: UGC data will always be displayed independently. It does not contribute to any aggregates in opus.'
      : null
    : 'Note: Streaming KPIs totals are an aggregated number of all available DSP data. UGC data is not included in these totals.';

  useEffect(() => {
    const args = {
      countryIds: compareMode === 'country' ? countryIds : filters?.country,
      customerFilter: filters?.streaming,
      mediaTypes: filters?.media,
      entityIds,
      entityType,
      region: filters?.region,
      customRegion: customRegionFilter,
      compareMode,
    };

    fetchStreamingData({ ...args });
  }, [
    compareMode,
    countryIds,
    entityIds,
    entityType,
    fetchStreamingData,
    filters,
    customRegionFilter,
  ]);

  const [thisWeekToDate, lastWeek] = useDateRangeKPI('streaming');

  const [, , , reason] = useCombinedRefetchState([streamingData]);

  const mappedStreamingData: any = mapKPIData({
    data: streamingData.value,
    compareMode,
    compareArray,
  });

  // key-value group items
  const streamingThisWeekToDateKeyValueGroupItems = [
    {
      label: 'Streams',
      metric: mappedStreamingData?.count_this_week_to_date,
    },
    {
      label: compareMode ? 'Streams' : 'Previous',
      metric: mappedStreamingData?.count_last_week_to_date,
    },
    {
      label: 'Change',
      metric: mappedStreamingData?.wow_in_progress,
    },
  ];

  const streamingHasLastFullWeekKeyValueGroupItems = [
    {
      label: 'Streams',
      metric: mappedStreamingData?.count_last_week,
    },
    {
      label: compareMode ? 'Streams' : 'Previous',
      metric: mappedStreamingData?.count_last_last_week,
    },
    {
      label: 'Change',
      metric: mappedStreamingData?.wow_last_completed,
    },
  ];

  const streamingHasReleaseToDateKeyValueGroupItems = [
    {
      label: 'Streams',
      metric: mappedStreamingData?.count_rtd,
    },
  ];

  PromiseState.all([
    topCountryThisWeekFetch,
    topCountryLastFullWeekFetch,
    topCountryReleaseToDateFetch,
  ]);

  const [topCountryThisWeek, topCountryLastFullWeek, topCountryReleaseToDate] =
    useMemo(() => {
      const metricValues = [
        topCountryThisWeekFetch.value,
        topCountryLastFullWeekFetch.value,
        topCountryReleaseToDateFetch.value,
      ];

      /**
       * Sorts items based on their entity id.
       * @param {object} a entity a
       * @param {object} b entity b
       */
      function byEntityId(a: IEntities, b: IEntities): number {
        return entityIds.indexOf(a.entity.id) - entityIds.indexOf(b.entity.id);
      }

      // Map through the api data, and return sorted values if the data is loaded.
      return metricValues.map((metric) => {
        if (metric) {
          return metric.sort(byEntityId);
        }

        return metric;
      });
    }, [
      entityIds,
      topCountryLastFullWeekFetch.value,
      topCountryReleaseToDateFetch.value,
      topCountryThisWeekFetch.value,
    ]);

  const addMetadata = (entity: any): any => {
    const byCountryId =
      (countryId: string) =>
      (el: any): boolean =>
        el.id === countryId;

    return {
      ...entity,
      data: entity.data.map((country: any) => ({
        ...country,
        metadata: countriesMetadata.find(byCountryId(country.countryId)),
      })),
    };
  };

  // Add metadata with flags
  const sortedTopCountryThisWeek = topCountryThisWeek?.map(addMetadata);
  const sortedTopCountryLastFullWeek = topCountryLastFullWeek?.map(addMetadata);
  const sortedTopCountryReleaseToDate =
    topCountryReleaseToDate?.map(addMetadata);

  const getCountryKey = useCallback(
    (entity: ITopTerritoriesEntity, item: ITopTerritoriesEntityCountryItem) =>
      `top-country-${entity.entity.id}-${item.countryName}`,
    []
  );

  const onClick = (): void =>
    topTerritoriesRef &&
    window.scrollTo({
      top: topTerritoriesRef.current.offsetTop + 150,
      behavior: 'smooth',
    });

  return (
    <Container
      isMobileView={isMobileView}
      data-pw={stringToSlug('Streaming KPIs')}
    >
      <ErrorBoundary
        error={
          streamingData.reason?.cause?.reason === 'invalidCustomer'
            ? undefined
            : reason
        }
      >
        <KPIWrapper
          allowedFilters={allowedFilters}
          glossarySlug={glossarySlug}
          title="Streaming KPIs"
          icon={StreamingIcon}
          extraToolbarItems={[
            {
              label: 'Export Streaming KPI data',
              href: streamingExportUrl,
              type: IToolbarItemType.Export,
              order: 1,
            },
          ]}
          isMobileView={isMobileView}
          iconWidth={23}
          iconHeight={23}
          titleAlignLeft="-1.5em"
          alignSVG="1.7em"
          newDesign
        >
          {streamingData.rejected ? (
            <ErrorMessage>{streamingData.reason.message}</ErrorMessage>
          ) : (
            <KPIList marginTop="28px" marginBottom="28px">
              <KPIListItem>
                <DataWrapper isMobileView={isMobileView} isHorizontalLineShown>
                  <Header
                    display="block"
                    marginBottom={isMobileView && '-1.5em'}
                  >
                    <Box display="flex" alignItems="center">
                      {`This Week ${!isMobileView ? 'to Date' : ''}`}
                      <Box width="0.875rem" height="0.875rem" ml={2}>
                        <TippyToolTip
                          enabled
                          tooltipContent={tooltipContent}
                          offset={[10, 25]}
                          maxWidth="170px"
                        >
                          <InfoIcon />
                        </TippyToolTip>
                      </Box>
                    </Box>
                    {thisWeekToDate && (
                      <DateRange>
                        {`${formatDate(thisWeekToDate?.startDate, 'day', true)} 
                        - ${formatDate(thisWeekToDate?.endDate, 'day', true)}`}
                      </DateRange>
                    )}
                  </Header>
                  {streamingData.pending &&
                    (isMobileView ? (
                      <StreamingKPILoader />
                    ) : (
                      <KPILoader showPrevious showChange showColumn />
                    ))}
                  {streamingData.fulfilled && !streamingData.pending && (
                    <KeyValueGroup
                      items={streamingThisWeekToDateKeyValueGroupItems}
                      dataColors={colors}
                      compareMode={compareMode}
                      showCyAttr={true}
                      isMobileView={isMobileView}
                      isFullNumber={false}
                      additionalHeader={compareMode && 'Previous Week to Date'}
                      showColumn
                      newDesign
                      isKPIValue
                    />
                  )}

                  <TopTerritories
                    label="Top Territories"
                    pending={topCountryThisWeekFetch?.pending}
                    topTerritories={sortedTopCountryThisWeek}
                    TerritoryItem={TopCountry}
                    getKey={getCountryKey}
                    onClick={onClick}
                    isMobileView={isMobileView}
                    marginTop="30px"
                    isStreaming
                    isFlexWrap
                  />
                </DataWrapper>
              </KPIListItem>
              <KPIListItem>
                <DataWrapper isMobileView={isMobileView} isHorizontalLineShown>
                  <Header
                    display="block"
                    marginBottom={isMobileView && '-1.5em'}
                  >
                    {`Last ${!isMobileView ? 'Full ' : ''}Week`}

                    {lastWeek && (
                      <DateRange>
                        {`${formatDate(lastWeek?.startDate, 'day', true)} 
                        - ${formatDate(lastWeek?.endDate, 'day', true)}`}
                      </DateRange>
                    )}
                  </Header>
                  {streamingData.pending &&
                    (isMobileView ? (
                      <StreamingKPILoader />
                    ) : (
                      <KPILoader showPrevious showChange showColumn />
                    ))}
                  {streamingData.fulfilled && mappedStreamingData && (
                    <KeyValueGroup
                      items={streamingHasLastFullWeekKeyValueGroupItems}
                      dataColors={colors}
                      compareMode={compareMode}
                      isMobileView={isMobileView}
                      isFullNumber={false}
                      additionalHeader={compareMode && 'Previous Full Week'}
                      showCyAttr
                      showColumn
                      newDesign
                      isKPIValue
                    />
                  )}
                  <TopTerritories
                    label="Top Territories"
                    pending={topCountryLastFullWeekFetch?.pending}
                    topTerritories={sortedTopCountryLastFullWeek}
                    TerritoryItem={TopCountry}
                    getKey={getCountryKey}
                    onClick={onClick}
                    isMobileView={isMobileView}
                    marginTop="30px"
                    isStreaming
                    isFlexWrap
                  />
                </DataWrapper>
              </KPIListItem>
              <KPIListItem>
                <DataWrapper isMobileView={isMobileView}>
                  <Header
                    display="flex"
                    marginBottom={
                      isMobileView && (compareMode ? '-1.5em' : '-0.5em')
                    }
                  >
                    Release to Date
                  </Header>
                  {streamingData.pending &&
                    (isMobileView ? (
                      <StreamingKPILoader oneLine />
                    ) : (
                      <KPILoader showColumn />
                    ))}
                  {streamingData.fulfilled && mappedStreamingData && (
                    <KeyValueGroup
                      items={streamingHasReleaseToDateKeyValueGroupItems}
                      dataColors={colors}
                      compareMode={compareMode}
                      isMobileView={isMobileView}
                      isFullNumber={false}
                      showColumn={!!compareMode}
                      showCyAttr
                      newDesign
                    />
                  )}
                  <TopTerritories
                    label="Top Territories"
                    pending={topCountryReleaseToDateFetch?.pending}
                    topTerritories={sortedTopCountryReleaseToDate}
                    TerritoryItem={TopCountry}
                    getKey={getCountryKey}
                    onClick={onClick}
                    isMobileView={isMobileView}
                    marginTop={
                      isMobileView || streamingData.pending ? '30px' : '93px'
                    }
                    isStreaming
                    isFlexWrap
                  />
                </DataWrapper>
              </KPIListItem>
            </KPIList>
          )}

          {warningMessage && (
            <DataWarning newDesign>{warningMessage}</DataWarning>
          )}
        </KPIWrapper>
      </ErrorBoundary>
    </Container>
  );
};

interface ICustomerExtended extends ICustomer {
  thumbnailUrl: string;
}

interface IApiConnectProps {
  entityType: EntityTypes;
  entityIds: string[];
  countryIds: string[];
  isExternalUser: boolean;
  filters?: ITopicFilterState;
  customers: ICustomerExtended[];
  compareMode?: string;
  isCustomersLoaded?: boolean;
}

export default compose<IApiConnectProps, any>(
  withEntityContext,
  connect(
    (state: RootState) => ({
      countries: state.countries,
      customers: selectAllCustomers(state),
      isCustomersLoaded: isCustomersLoaded(state),
    }),
    { loadCountries, loadCustomers }
  ),
  apiConnect(
    ({
      entityType,
      entityIds,
      countryIds,
      isExternalUser,
      filters = {} as ITopicFilterState,
      customers,
      compareMode,
      isCustomersLoaded,
    }: IApiConnectProps): any => {
      const initialSalesType = getInitialSalesType(isExternalUser, entityType);

      const availableCustomerFilters = getAllowedCustomerFilters(
        initialSalesType as any,
        entityType as any
      );

      const isCustomerFilterApplied = availableCustomerFilters.some((el: any) => !!(filters as any)?.[el])

      const customerFilter = getCustomerFilters(
        customers,
        availableCustomerFilters,
        filters
      );

      /**
       * Limit effects the number of items returned per entity.
       * It does not limit the number of entities (country/entity compare) you can retrieve.
       */
      let limit = 5;
      if (compareMode === 'country') {
        limit = countryIds.length;
      }

      const getUrl = (args: {
        sortMetric: 'twtd' | 'rtd' | 'lw';
        limit: number;
      }): string =>
        (isCustomerFilterApplied && isCustomersLoaded || !isCustomerFilterApplied) ?
        getTerritoryKPIURL({
          entityIds,
          entityType,
          sortOrder: 'desc',
          countryIds: compareMode === 'country' ? countryIds : filters.country,
          customerFilter,
          salesType: initialSalesType,
          mediaTypes: filters?.media,
          region: filters?.region,
          customRegion: filters?.customRegion,
          ...args,
        }) : '';

      return {
        topCountryThisWeekFetch: getUrl({
          sortMetric: 'twtd',
          limit,
        }),
        topCountryLastFullWeekFetch: getUrl({
          sortMetric: 'lw',
          limit,
        }),
        topCountryReleaseToDateFetch: getUrl({
          sortMetric: 'rtd',
          limit,
        }),
        fetchStreamingData: (args: any): any => {
          return { streamingData: getStreamingKPIURL(args) };
        },
      };
    }
  )
)(StreamingKPIs as any) as any;
